import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { formatDate, formatNOK } from "@/lib/format";
import type { EquipmentPatch } from "@/hooks/use-equipment-mutations";
import type { InsuranceState } from "@/lib/equipment/insurance";
import type { DBEquipmentItem } from "@/lib/db-types";
import { InsuranceRowActions } from "./InsuranceRowActions";

export interface InsuranceRow {
  item: DBEquipmentItem;
  state: InsuranceState;
}

interface InsuranceTableProps {
  rows: InsuranceRow[];
  today: string;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  categoryName: (id: string) => string;
  departmentName: (id: string | null) => string;
  disabled: boolean;
  onPatch: (ids: string[], patch: EquipmentPatch, message: string) => void;
}

const th = "py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap";

function InsuredUntil({ row }: { row: InsuranceRow }) {
  const { item, state } = row;
  if (state === "expiring") return <StatusBadge tone="near" label={formatDate(item.insured_until)} />;
  if (state === "not_insured") return <StatusBadge tone="miss" label="Ikke forsikret" />;
  if (state === "insured") return <StatusBadge tone="reached" label={formatDate(item.insured_until)} />;
  return <span className="text-xs text-muted-foreground">{item.insurance_declined ? "Ikke forny" : "–"}</span>;
}

export function InsuranceTable(props: InsuranceTableProps) {
  const { rows, selected } = props;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground p-4">Ingenting her.</p>;
  const allSelected = rows.every((r) => selected.has(r.item.id));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="border-b">
            <th className={`${th} w-8`}>
              <Checkbox checked={allSelected} onCheckedChange={props.onToggleAll} aria-label="Velg alle" />
            </th>
            <th className={`${th} text-left`}>Navn</th>
            <th className={`${th} text-left`}>Kategori</th>
            <th className={`${th} text-left`}>Sted</th>
            <th className={`${th} text-right`}>Forsikringsverdi</th>
            <th className={`${th} text-left`}>Forsikret til</th>
            <th className={`${th} text-right`}>Valg</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.item.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
              <td className="py-2.5 px-3">
                <Checkbox
                  checked={selected.has(row.item.id)}
                  onCheckedChange={() => props.onToggle(row.item.id)}
                  aria-label={`Velg ${row.item.name}`}
                />
              </td>
              <td className="py-2.5 px-3">
                <Link to={`/utstyr/${row.item.id}`} className="font-medium hover:underline">{row.item.name}</Link>
                {row.item.comment && <div className="text-xs text-muted-foreground line-clamp-1">{row.item.comment}</div>}
              </td>
              <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{props.categoryName(row.item.category_id)}</td>
              <td className="py-2.5 px-3 text-muted-foreground">{props.departmentName(row.item.department_id)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
                {row.item.insurance_value != null ? formatNOK(row.item.insurance_value) : "–"}
              </td>
              <td className="py-2.5 px-3"><InsuredUntil row={row} /></td>
              <td className="py-2.5 px-3">
                <InsuranceRowActions
                  item={row.item}
                  state={row.state}
                  today={props.today}
                  disabled={props.disabled}
                  onPatch={(patch, message) => props.onPatch([row.item.id], patch, message)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
